import {
  type Column,
  flexRender,
  type Header,
  type RowData,
  Subscribe,
} from '@tanstack/react-table';
import { cn } from 'cn';
import type { AriaAttributes, CSSProperties, ReactNode } from 'react';
import { match } from 'ts-pattern';
import { Tooltip } from '../../Tooltip/Tooltip';
import type { kkTableFeatures, TableInstance } from '../tableFeatures';
import type { ColumnSortDirection } from './columnHeaderControlsShared';
import { TableHeaderControls } from './TableHeaderControls';

interface TableHeaderProps<TData extends RowData> {
  data: TData[];
  filterable?: boolean;
  getHeaderClassName?: (
    header: Header<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  gridTemplateColumns?: string;
  isVirtualized: boolean;
  sortable?: boolean;
  stickyHeader?: boolean;
  table: TableInstance<TData>;
}

interface TableHeaderCellProps<TData extends RowData> {
  data: TData[];
  filterable?: boolean;
  getHeaderClassName?: (
    header: Header<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  header: Header<typeof kkTableFeatures, TData, unknown>;
  isVirtualized: boolean;
  sortable?: boolean;
  table: TableInstance<TData>;
}

function getCommonPinningStyles<TData extends RowData>(
  column: Column<typeof kkTableFeatures, TData, unknown>,
): CSSProperties {
  const isPinned = column.getIsPinned();

  return {
    insetInlineStart:
      isPinned === 'start' ? `${column.getStart('start')}px` : undefined,
    insetInlineEnd:
      isPinned === 'end' ? `${column.getAfter('end')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
}

function getHeaderControlState<TData extends RowData>(
  header: Header<typeof kkTableFeatures, TData, unknown>,
  filterable?: boolean,
  sortable?: boolean,
) {
  return {
    isFilterable: Boolean(filterable && header.column.getCanFilter()),
    isSortable: Boolean(
      sortable &&
        header.column.getCanSort() &&
        header.column.columnDef.meta?.hideSorting !== true,
    ),
  };
}

function getHeaderCellAttributes<TData extends RowData>(
  header: Header<typeof kkTableFeatures, TData, unknown>,
) {
  const { column } = header;
  const isPinned = column.getIsPinned();

  return {
    'data-is-first-right-pinned-column':
      isPinned === 'end' && column.getIsFirstColumn('end') ? 'true' : undefined,
    'data-is-last-left-pinned-column':
      isPinned === 'start' && column.getIsLastColumn('start')
        ? 'true'
        : undefined,
    'data-is-pinned': isPinned ? 'true' : undefined,
  };
}

function getHeaderCellStyle<TData extends RowData>(
  header: Header<typeof kkTableFeatures, TData, unknown>,
  isVirtualized: boolean,
): CSSProperties {
  if (isVirtualized) return { position: 'relative' };

  return {
    width: `${header.getSize()}px`,
    ...getCommonPinningStyles(header.column),
    position: 'sticky',
    top: 0,
  };
}

function getHeaderSort<TData extends RowData>(
  header: Header<typeof kkTableFeatures, TData, unknown>,
  sortable: boolean | undefined,
  sortDirection: ColumnSortDirection,
): AriaAttributes['aria-sort'] | undefined {
  if (!sortable || !header.column.getCanSort()) return undefined;

  return match(sortDirection)
    .returnType<AriaAttributes['aria-sort']>()
    .with('asc', () => 'ascending')
    .with('desc', () => 'descending')
    .otherwise(() => 'none');
}

function getHeaderLayoutClass<TData extends RowData>(
  header: Header<typeof kkTableFeatures, TData, unknown>,
  filterable?: boolean,
  sortable?: boolean,
): string {
  if (
    sortable &&
    header.column.getCanSort() &&
    filterable &&
    header.column.getCanFilter()
  ) {
    return 'laane:space-x-1';
  }

  return 'space-x-2';
}

function getHeaderLabel<TData extends RowData>(
  header: Header<typeof kkTableFeatures, TData, unknown>,
): ReactNode {
  if (header.isPlaceholder) return null;

  return flexRender(header.column.columnDef.header, header.getContext());
}

function TableHeaderCell<TData extends RowData>({
  data,
  filterable,
  getHeaderClassName,
  header,
  isVirtualized,
  sortable,
  table,
}: TableHeaderCellProps<TData>) {
  return (
    <Subscribe
      source={table.store}
      selector={(state) => {
        const sorting = state.sorting.find(({ id }) => id === header.column.id);
        return sorting ? (sorting.desc ? 'desc' : 'asc') : false;
      }}
    >
      {(sortDirection) => {
        const { isFilterable, isSortable } = getHeaderControlState(
          header,
          filterable,
          sortable,
        );

        return (
          <th
            {...getHeaderCellAttributes(header)}
            aria-sort={getHeaderSort(header, sortable, sortDirection)}
            className={getHeaderClassName?.(header)}
            data-testid={`header-${header.column.id}`}
            role="columnheader"
            style={getHeaderCellStyle(header, isVirtualized)}
          >
            <Tooltip
              enabled={Boolean(header.column.columnDef.meta?.tooltip)}
              tooltip={header.column.columnDef.meta?.tooltip}
            >
              <div
                className={cn(
                  'flex items-end',
                  { 'justify-end': header.column.columnDef.meta?.rightAlign },
                  getHeaderLayoutClass(header, filterable, sortable),
                )}
              >
                <TableHeaderControls<TData>
                  column={header.column}
                  data={data}
                  isFilterable={isFilterable}
                  isSortable={isSortable}
                  label={getHeaderLabel(header)}
                  sortDirection={sortDirection}
                  table={table}
                />
              </div>
            </Tooltip>
          </th>
        );
      }}
    </Subscribe>
  );
}

export function TableHeader<TData extends RowData>({
  data,
  filterable,
  getHeaderClassName,
  gridTemplateColumns,
  isVirtualized,
  sortable,
  stickyHeader,
  table,
}: TableHeaderProps<TData>) {
  return (
    <thead
      data-is-sticky={stickyHeader ? 'true' : undefined}
      className={cn(
        'group/header',
        isVirtualized && 'bg-neutral-5 kunde:bg-blue-5',
      )}
      style={
        isVirtualized
          ? {
              display: 'grid',
              position: 'sticky',
              top: 0,
              width: '100%',
              zIndex: 5,
            }
          : undefined
      }
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <tr
          key={headerGroup.id}
          className="group/row"
          style={
            isVirtualized
              ? {
                  display: 'grid',
                  gridTemplateColumns,
                  width: '100%',
                }
              : undefined
          }
        >
          {headerGroup.headers.map((header) => (
            <TableHeaderCell<TData>
              data={data}
              filterable={filterable}
              getHeaderClassName={getHeaderClassName}
              header={header}
              isVirtualized={isVirtualized}
              key={header.id}
              sortable={sortable}
              table={table}
            />
          ))}
        </tr>
      ))}
    </thead>
  );
}
